import {
  IDE_PART_TYPES
} from 'pw-drafty'
import {
  CASE_INSENSITIVE,
  VALIDATE_WITHOUT_QUOTES,
  QUERY_INPUT_TYPE_NAMES,
  QUERY_OPERATORS_TO_QUERY_STATE,
  QUERY_OPERATOR_TOOLTIPS,
  QUERY_PART_CONJUNCTIONS,
  QUERY_PART_TYPES,
  DEFAULT_CONJUNCTION,
  QUERY_OPERATOR_START_CHARS,
  QUERY_OPERATOR_END_CHARS,
  QUERY_INPUT_SCHEMA_DEFAULTS
} from './constants'
import {
  splitOutsideQuotes
} from 'pw-validators'

const CONJUNCTION_OPTIONS = [
  {
    name: 'AND',
    id: 'AND',
    needsSpaceAfter: true
  },
  {
    name: 'OR',
    id: 'OR',
    needsSpaceAfter: true,
    // dividerAfter: true
  }
]

/**
 * Return the QueryState value for a given raw input value string
 * @param {string} valueString - Raw "value" input string
 * @param {object} schemaDef - Schema definition for the passed value.
 * @returns {string|number} query value
 */
function getQueryStateValue (valueString, schemaDef) {
  const valOrigCasing = valueString
  const val = CASE_INSENSITIVE ? valueString.toLowerCase() : valueString
  const valWithoutQuotes = val.trim().replace(/"/gi, '')

  if (schemaDef.type === QUERY_INPUT_TYPE_NAMES.ENUM && schemaDef.optionsToOutput && schemaDef.optionsToOutput[valWithoutQuotes]) {
    return schemaDef.optionsToOutput[valWithoutQuotes] // Return value mapped back to Schema-defined server value
  }
  if (schemaDef.type === QUERY_INPUT_TYPE_NAMES.DATETIME) {
    return +new Date(valWithoutQuotes) // Return value parsed as unix timestamp
  }
  if (schemaDef.transformQueryValue) {
    return schemaDef.transformQueryValue(valWithoutQuotes)
  }
  else {
    return valOrigCasing
  }
}


/**
 * Parse a raw string, returning an array of query values
 * @param {string} valueString
 * @returns {array|string} parsed values
 */
function getQueryStateValues (valueString, schemaDef) {
  const values = splitOutsideQuotes(valueString, ',')
  return values && values.length > 1 ? values.map(v => getQueryStateValue(v.match.trim(), schemaDef)) : getQueryStateValue(valueString.trim(), schemaDef)
}


/**
 * Return a QueryPart for the passed clause/schema components
 * @param {object} schemaDef Schema definition
 * @param {string} key
 * @param {string} operator
 * @param {string} value
 */
function getQueryPartFromClause (schemaDef, key, operator, value) {
  const queryStateOperator = QUERY_OPERATORS_TO_QUERY_STATE[operator]
  const queryStateValue = getQueryStateValues(value, schemaDef)
  const hasMultipleValues = Array.isArray(queryStateValue) && queryStateValue.length > 1

  let queryPart = {
    name: schemaDef.fieldName || key,
    op: (queryStateOperator === 'eq' || queryStateOperator === '!eq') && hasMultipleValues ? 'in' : queryStateOperator,
    value: queryStateValue
  }

  if (queryStateOperator === '!eq' && hasMultipleValues) {
    queryPart = {
      'not': queryPart // Negate whole clause
    }
  }

  return queryPart
}


/**
 * Parse an input string and return a queryState suitable for transmission to PW Query API,
 * an editorState suitable for passing to QueryState.jsx for text highlighting and rendering,
 * and validationMessages, an accumulation of error/warning messages about the parsing status.
 * @param {string} query The raw PW Query input string
 * @param {object} schema The PW Query fields schema
 * @returns {object}
 */
export function parseQuery (query = "", caretOffset = -1, schema = QUERY_INPUT_SCHEMA_DEFAULTS, allowUnknownFields = false, optionMaps = {}) {
  const {
    fields=[],
    operatorsByField={},
    valuesByField={}
  } = optionMaps

  const validationMessages = []
  let typeaheadState = null
  const queryLen = query.length

  let currentIndex = 0
  let current = ""
  let prev = ""

  let currentKey = ""
  let key = ""
  let currentOperator = ""
  let operator = ""
  let currentValue = ""
  let value = ""

  const prevConjunctionIds = []
  let currentConjunctionId = null

  let isInQuoteScope = false
  let needsTypeaheadRangeSeal = false

  const outputEditorParts = []
  const outputQueryClauses = []

  const clauseNests = []
  const clauseNestStartOffsets = []
  var currentClauses = []
  let nextEditorPartShouldBeClause = true

  function _trimKey (keyStr) {
    return (CASE_INSENSITIVE ? keyStr.toLowerCase() : keyStr).trim()
  }

  function _reset () {
    // Reset all transitory variables
    currentKey = ""
    key = ""
    currentOperator = ""
    operator = ""
    currentValue = ""
    value = ""
  }

  function _setTypeaheadState (matchText, typeaheadOptions, typeaheadOptionKey) {
    const isAtCaretOffset = caretOffset > -1 && currentIndex === caretOffset

    if (!isAtCaretOffset) {
      return
    }
    // No options supplied, fall back to either field or conjunction based on expected alternating order
    if (!typeaheadOptions) {
      if (nextEditorPartShouldBeClause) {
        typeaheadOptions = fields
      }
      else {
        typeaheadOptions = CONJUNCTION_OPTIONS
      }
    }

    if (typeaheadOptionKey) {
      typeaheadOptions = typeaheadOptions[_trimKey(typeaheadOptionKey)]
    }

    needsTypeaheadRangeSeal = true

    const typeaheadRange = [currentIndex - matchText.length]

    typeaheadState = {
      matchText,
      typeaheadOptions,
      typeaheadRange
    }
  }

  function _sealTypeaheadRange () {
    if (needsTypeaheadRangeSeal) {
      typeaheadState.typeaheadRange.push(currentIndex)
      needsTypeaheadRangeSeal = false
    }
  }

  function _pushClause () {
    // Query Part (Key/Operator/Value) is complete
    if (!isInQuoteScope) {
      value = currentValue
    }
    _sealTypeaheadRange()

    const fullClauseString = `${key}${operator}${value}`
    const offset = currentIndex - fullClauseString.length
    const trimmedKey = _trimKey(key)
    const trimmedOperator = operator.trim()
    const trimmedValue = value.trim()

    const schemaDef = schema[trimmedKey] || schema['unknown']

    const queryPart = getQueryPartFromClause(schemaDef, trimmedKey, trimmedOperator, trimmedValue)

    const valueCommaIdx = value.lastIndexOf(",")

    if (schemaDef.fieldName === "unknown") {
      _setTypeaheadState(key)
    }
    else if (!operator) {
      _setTypeaheadState(currentOperator, operatorsByField, key)
    }
    else if (!value) {
      _setTypeaheadState(currentValue, valuesByField, key)
    }
    else if (valueCommaIdx !== -1) {
      // Last value character was a comma
      _setTypeaheadState(value.slice(valueCommaIdx + 1), valuesByField, key)
    }

    // Clause validation
    let invalidKeyMsg = null
    let invalidOperatorMsg = null
    let invalidValueMsg = null

    if (!allowUnknownFields && schemaDef.fieldName === 'unknown') {
      invalidKeyMsg = "Unknown Field"
    }
    if (schemaDef.validateOperator && operator) {
      invalidOperatorMsg = schemaDef.validateOperator(trimmedOperator)
      if (invalidOperatorMsg) {
        validationMessages.push(`Invalid Operator - ${ schemaDef.label } - ${ invalidOperatorMsg }`)
      }
    }
    if (schemaDef.validate && value) {
      let validationVal = CASE_INSENSITIVE ? value.toLowerCase() : value
      if (VALIDATE_WITHOUT_QUOTES) {
        validationVal = validationVal.replace(/"/g, '')
      }
      invalidValueMsg = schemaDef.validate(validationVal)
      if (invalidValueMsg) {
        validationMessages.push(`Invalid Value - ${ schemaDef.label } - ${ invalidValueMsg }`)
      }
    }

    if (!invalidKeyMsg) {
      invalidKeyMsg = !operator ? "Operator is required" : !value ? "Value is required" : null
    }
    if (invalidKeyMsg) {
      validationMessages.push(`Invalid Field - ${ trimmedKey } - ${ invalidKeyMsg }`)
    }

    const keyLen = key.length
    const operatorLen = operator.length

    const editorPart = {
      partType: IDE_PART_TYPES.KEY_VALUE,
      text: fullClauseString,
      offset: offset,
      length: fullClauseString.length,

      className: schemaDef.className,
      tooltip: schemaDef.tooltip,

      queryStatePart: queryPart,
      queryPartType: QUERY_PART_TYPES.CLAUSE,

      children: {
        key: {
          offset: offset,
          length: keyLen,
          text: key,
          queryPartType: QUERY_PART_TYPES.FIELD,
          className: schemaDef.children.key.className,
          tooltip: schemaDef.tooltip,
          invalidMsg: invalidKeyMsg
        },
        operator: {
          offset: offset + keyLen,
          length: operatorLen,
          text: operator,
          queryPartType: QUERY_PART_TYPES.OPERATOR,
          className: schemaDef.children.operator.className,
          tooltip: QUERY_OPERATOR_TOOLTIPS[operator],
          invalidMsg: invalidOperatorMsg
        },
        value: {
          offset: offset + operatorLen + keyLen,
          length: value.length,
          text: value,
          queryPartType: QUERY_PART_TYPES.VALUE,
          className: schemaDef.children.value.className,
          tooltip: schemaDef.tooltip,
          invalidMsg: invalidValueMsg
        }
      }
    }

    outputEditorParts.push(editorPart)
    currentClauses.push(queryPart)

    nextEditorPartShouldBeClause = false

    _reset()
  }

  function _pushConjunction () {
    const conjunction = currentKey
    const conjunctionID = QUERY_PART_CONJUNCTIONS[conjunction]
    let conjunctionInvalidMsg = null

    if (!conjunctionID) {
      conjunctionInvalidMsg = `Valid Conjunctions: ${Object.keys(QUERY_PART_CONJUNCTIONS).join(', ')}`
      validationMessages.push(`Invalid Conjunction - ${currentKey} - ${conjunctionInvalidMsg}`)
    }
    else if (!currentConjunctionId) {
      currentConjunctionId = conjunctionID // First conjunction encountered, set it as current for all subsequent comparisons
    }
    else if (conjunctionID !== currentConjunctionId) {
      conjunctionInvalidMsg = "Mixed conjunctions (AND/OR) not allowed at the same query depth. Use parentheses to define a new depth."
      validationMessages.push(`Invalid Conjunction - ${currentKey} - ${ conjunctionInvalidMsg }`)
    }

    outputEditorParts.push({
      partType: IDE_PART_TYPES.VALUE,
      text: conjunction,
      offset: currentIndex - conjunction.length,
      length: conjunction.length,
      queryPartType: QUERY_PART_TYPES.CONJUNCTION,

      tooltip: schema['conjunction'].tooltip,
      className: schema['conjunction'].className,
      invalidMsg: conjunctionInvalidMsg
    })

    nextEditorPartShouldBeClause = true
    _reset()
    _sealTypeaheadRange()
  }

  function _openNest () {
    // New nesting depth
    prevConjunctionIds.push(currentConjunctionId)

    // Take all current clauses and push them onto the "outer" nest stack
    clauseNests.push(currentClauses)
    clauseNestStartOffsets.push(currentIndex)

    currentConjunctionId = null // Reset to allow this nesting group to specify a new conjunction
    currentClauses = [] // Fresh clauses for new container
    nextEditorPartShouldBeClause = true
  }

  function _closeNest () {
    if (clauseNests.length) {
      const parentClauses = clauseNests.pop()
      const startOffset = clauseNestStartOffsets.pop()
      const endOffset = currentIndex + 1

      // Put the now closed nesting group into the parent's clause set
      parentClauses.push({
        [currentConjunctionId]: currentClauses
      })

      const nestSubstr = query.substring(startOffset, endOffset)
      outputEditorParts.push({
        partType: IDE_PART_TYPES.GROUP,
        queryPartType: QUERY_PART_TYPES.GROUP,
        text: nestSubstr,
        offset: startOffset,
        length: nestSubstr.length
      })

      // Move back "up" to the outer nesting context
      currentClauses = parentClauses
      currentConjunctionId = prevConjunctionIds.pop() // Set to the next outer conjunction ID
      nextEditorPartShouldBeClause = false
    } else {
      const invalidMsg = `Unmatched closing parenthesis`
      validationMessages.push(`Invalid Nesting - ) - ${invalidMsg}`)
      outputEditorParts.push({
        partType: IDE_PART_TYPES.INVALID_RANGE,
        text: query.substr(currentIndex, 1),
        offset: currentIndex,
        length: 1,
        invalidMsg: invalidMsg
      })
    }
  }

  function _pushPartialClause () {
    key = currentKey
    operator = currentOperator
    value = currentValue
    if (key) {
      _pushClause()
    }
    _sealTypeaheadRange()
  }

  // Main loop, run for each character in the query string
  while (currentIndex !== queryLen) {
    current = query[currentIndex]

    // Toggle this flag on each unescaped double quote encountered
    if (current === '"' && prev !== "\\") {
      isInQuoteScope = !isInQuoteScope
    }

    if (current === ',' && !isInQuoteScope) {
      _sealTypeaheadRange()
    }

    // Main character handlers
    if (current === "(" && !isInQuoteScope) {
      _setTypeaheadState(currentKey)
      _openNest()
    }
    else if (current === ")" && !isInQuoteScope) {
      _setTypeaheadState(currentKey)
      if (currentKey) {
        _pushPartialClause() // Close off any current clause
      }
      _closeNest()
    }
    else if (current === " ") {
      // Comma-separated multi-values can optionally contain commas with trailing spaces
      const spaceIsStillValue = isInQuoteScope || prev === ','

      if (spaceIsStillValue) {
        // Still inside an unescaped double-quote scope, continue building value string
        currentValue += current
      }
      else if (key && operator && currentValue) {
        _setTypeaheadState(currentValue, valuesByField, key)
        _pushClause() // Done with Clause
      }
      else if (currentKey && !currentOperator) {
        _setTypeaheadState(currentKey)
        if (nextEditorPartShouldBeClause) {
          _pushPartialClause()
        }
        else {
          _pushConjunction()
        }
      }
      else {
        // Unexpected space, lop everything off and treat as a clause
        _setTypeaheadState(currentKey)
        if (currentKey) {
          _pushPartialClause()
        }
      }
    }
    else if (key === "") {
      if (QUERY_OPERATOR_START_CHARS.includes(current)) {
        key = currentKey // Key is finished
        _sealTypeaheadRange()
        _setTypeaheadState("", operatorsByField, currentKey)
        currentOperator += current // Begin building operator
      }
      else {
        _setTypeaheadState(currentKey)
        currentKey += current
      }
    }
    else if (operator === "" && currentOperator !== "") {
      if (QUERY_OPERATOR_START_CHARS.includes(prev) && QUERY_OPERATOR_END_CHARS.includes(current)) {
        // This is the end of a two-character operator
        _setTypeaheadState(currentOperator, operatorsByField, currentKey)
        currentOperator += current
        operator = currentOperator
        _sealTypeaheadRange()

      }
      else {
        operator = currentOperator
        _sealTypeaheadRange()
        _setTypeaheadState(currentValue, valuesByField, key)
        currentValue += current
      }
    }
    else if (key !== "" && operator !== "") {
      const valueCommaIdx = currentValue.lastIndexOf(",")
      if (valueCommaIdx !== -1) {
        // Last value character was a comma
        _setTypeaheadState(currentValue.slice(valueCommaIdx + 1), valuesByField, key)
      }
      else {
        _setTypeaheadState(currentValue, valuesByField, key)
      }

      currentValue += current // Building value
    }
    else {
      console.warn('UNKNOWN CASE?', current)
    }

    prev = current
    currentIndex += 1
  }

  if (queryLen === 0 || (!isInQuoteScope && current === " ")) {
    // Empty input OR end with trailing space
    _setTypeaheadState(currentKey)
  }

  if (currentKey) {
    if (key && operator && currentValue) {
      _setTypeaheadState(currentValue, valuesByField, key)
    }
    _pushPartialClause()
  }
  _sealTypeaheadRange()

  // Handle unclosed parens
  while (clauseNestStartOffsets.length) {
    const invalidMsg = 'Unclosed opening parenthesis'
    const startOffset = clauseNestStartOffsets.pop()
    validationMessages.push(`Invalid Nesting - ( - ${invalidMsg}`)
    outputEditorParts.push({
      partType: IDE_PART_TYPES.INVALID_RANGE,
      text: query.substr(startOffset, 1),
      offset: startOffset,
      length: 1,
      invalidMsg: invalidMsg
    })
  }

  // Put outermost clauses into output
  if (currentClauses && currentClauses.length > 0) {
    outputQueryClauses.push({
      [currentConjunctionId || DEFAULT_CONJUNCTION]: currentClauses
    })
  }

  return {
    validationMessages,
    editorState: outputEditorParts,
    queryState: outputQueryClauses,
    typeaheadState
  }
}
